/**
 * Role-Based Permission Matrix
 *
 * DEPRECATED — This module-level matrix is no longer enforced at runtime.
 * The single source of truth for frontend + backend RBAC is now:
 *   • Frontend: PermissionsContext reading app_settings via SettingsService
 *   • Backend:  has_permission() / _rls_dyn() from app_settings matrix
 *
 * This file is preserved for compatibility with usePermissions hook only.
 * Do not add new code here.
 */
import type { UserRole } from "./RoleContext";

// ── Permission Layers ──

export interface ModulePermissions {
  view: boolean;
  interact: boolean;
  edit: boolean;
  approve: boolean;
}

// ── Module Keys ──

export type ModuleKey =
  | "command_center"
  | "command_dashboard"
  | "leads"
  | "conversations"
  | "follow_ups"
  | "appointments"
  | "appointment_results"
  | "opportunities"
  | "rep_scorecards"
  | "commissions"
  | "disposition_report"
  | "pricing"
  | "financials"
  | "accounting"
  | "revenue_intelligence"
  | "earnings_intelligence"
  | "jobs"
  | "dispatch"
  | "scheduling"
  | "install_readiness"
  | "issues"
  | "service_tickets"
  | "qa_form"
  | "reports"
  | "marketing"
  | "pipeline_intelligence"
  | "automations"
  | "warehouse"
  | "inventory"
  | "ordering"
  | "vendors"
  | "contractors"
  | "portals"
  | "settings"
  | "control_plane"
  | "catalog";

// ── Helper Constructors ──

const FULL: ModulePermissions = { view: true, interact: true, edit: true, approve: true };
const VIEW_INTERACT: ModulePermissions = { view: true, interact: true, edit: false, approve: false };
const VIEW_ONLY: ModulePermissions = { view: true, interact: false, edit: false, approve: false };
const NONE: ModulePermissions = { view: false, interact: false, edit: false, approve: false };
const VIEW_INTERACT_EDIT: ModulePermissions = { view: true, interact: true, edit: true, approve: false };

// ── Permission Matrix ──

const PERMISSION_MATRIX: Record<UserRole, Record<ModuleKey, ModulePermissions>> = {
  super_admin: Object.fromEntries(
    ([
      "command_center", "command_dashboard", "leads", "conversations", "follow_ups",
      "appointments", "appointment_results", "opportunities", "rep_scorecards",
      "commissions", "disposition_report", "pricing", "financials", "accounting",
      "revenue_intelligence", "earnings_intelligence", "jobs", "dispatch", "scheduling",
      "install_readiness", "issues", "service_tickets", "qa_form", "reports",
      "marketing", "pipeline_intelligence", "automations", "warehouse", "inventory",
      "ordering", "vendors", "contractors", "portals", "settings", "control_plane", "catalog",
    ] as ModuleKey[]).map((k) => [k, FULL])
  ) as Record<ModuleKey, ModulePermissions>,

  owner: Object.fromEntries(
    ([
      "command_center", "command_dashboard", "leads", "conversations", "follow_ups",
      "appointments", "appointment_results", "opportunities", "rep_scorecards",
      "commissions", "disposition_report", "financials", "accounting",
      "revenue_intelligence", "earnings_intelligence", "jobs", "dispatch", "scheduling",
      "install_readiness", "issues", "service_tickets", "qa_form", "reports",
      "marketing", "pipeline_intelligence", "automations", "warehouse", "inventory",
      "ordering", "vendors", "contractors", "portals", "settings", "control_plane", "catalog",
    ] as ModuleKey[]).map((k) => [k, FULL])
  ) as Record<ModuleKey, ModulePermissions>,

  sales_manager: {
    command_center: VIEW_INTERACT,
    command_dashboard: VIEW_ONLY,
    leads: VIEW_ONLY,
    conversations: VIEW_ONLY,
    follow_ups: VIEW_ONLY,
    appointments: VIEW_ONLY,
    appointment_results: VIEW_ONLY,
    opportunities: VIEW_ONLY,
    rep_scorecards: VIEW_INTERACT,
    commissions: VIEW_ONLY,
    disposition_report: VIEW_ONLY,
    pricing: NONE,
    financials: NONE,
    accounting: NONE,
    revenue_intelligence: NONE,
    earnings_intelligence: NONE,
    jobs: VIEW_ONLY,
    dispatch: NONE,
    scheduling: NONE,
    install_readiness: NONE,
    issues: VIEW_ONLY,
    service_tickets: NONE,
    qa_form: NONE,
    reports: VIEW_ONLY,
    marketing: VIEW_ONLY,
    pipeline_intelligence: VIEW_ONLY,
    automations: NONE,
    warehouse: NONE,
    inventory: NONE,
    ordering: NONE,
    vendors: NONE,
    contractors: NONE,
    portals: NONE,
    settings: NONE,
    control_plane: NONE,
    catalog: NONE,
  },

  sales: {
    command_center: VIEW_INTERACT,
    command_dashboard: NONE,
    leads: VIEW_INTERACT,
    conversations: VIEW_INTERACT_EDIT,
    follow_ups: VIEW_INTERACT_EDIT,
    appointments: VIEW_INTERACT_EDIT,
    appointment_results: VIEW_ONLY,
    opportunities: VIEW_INTERACT,
    rep_scorecards: VIEW_ONLY,
    commissions: NONE,
    disposition_report: NONE,
    pricing: NONE,
    financials: NONE,
    accounting: NONE,
    revenue_intelligence: NONE,
    earnings_intelligence: NONE,
    jobs: VIEW_ONLY,
    dispatch: NONE,
    scheduling: NONE,
    install_readiness: NONE,
    issues: VIEW_INTERACT,
    service_tickets: NONE,
    qa_form: NONE,
    reports: NONE,
    marketing: NONE,
    pipeline_intelligence: NONE,
    automations: NONE,
    warehouse: NONE,
    inventory: NONE,
    ordering: NONE,
    vendors: NONE,
    contractors: NONE,
    portals: NONE,
    settings: NONE,
    control_plane: NONE,
    catalog: NONE,
  },

  call_center: {
    command_center: NONE,
    command_dashboard: NONE,
    leads: VIEW_INTERACT_EDIT,
    conversations: VIEW_INTERACT_EDIT,
    follow_ups: VIEW_INTERACT_EDIT,
    appointments: VIEW_INTERACT,
    appointment_results: NONE,
    opportunities: NONE,
    rep_scorecards: NONE,
    commissions: NONE,
    disposition_report: NONE,
    pricing: NONE,
    financials: NONE,
    accounting: NONE,
    revenue_intelligence: NONE,
    earnings_intelligence: NONE,
    jobs: NONE,
    dispatch: NONE,
    scheduling: NONE,
    install_readiness: NONE,
    issues: NONE,
    service_tickets: NONE,
    qa_form: NONE,
    reports: NONE,
    marketing: NONE,
    pipeline_intelligence: NONE,
    automations: NONE,
    warehouse: NONE,
    inventory: NONE,
    ordering: NONE,
    vendors: NONE,
    contractors: NONE,
    portals: NONE,
    settings: NONE,
    control_plane: NONE,
    catalog: NONE,
  },

  ops_manager: {
    command_center: FULL,
    command_dashboard: VIEW_ONLY,
    leads: VIEW_INTERACT,
    conversations: VIEW_INTERACT,
    follow_ups: VIEW_INTERACT_EDIT,
    appointments: VIEW_INTERACT_EDIT,
    appointment_results: VIEW_ONLY,
    opportunities: VIEW_INTERACT,
    rep_scorecards: VIEW_ONLY,
    commissions: VIEW_ONLY,
    disposition_report: VIEW_ONLY,
    pricing: NONE,
    financials: NONE,
    accounting: VIEW_ONLY,
    revenue_intelligence: VIEW_ONLY,
    earnings_intelligence: NONE,
    jobs: FULL,
    dispatch: FULL,
    scheduling: FULL,
    install_readiness: FULL,
    issues: FULL,
    service_tickets: FULL,
    qa_form: VIEW_INTERACT_EDIT,
    reports: VIEW_ONLY,
    marketing: VIEW_ONLY,
    pipeline_intelligence: VIEW_ONLY,
    automations: VIEW_INTERACT_EDIT,
    warehouse: VIEW_INTERACT,
    inventory: VIEW_INTERACT,
    ordering: VIEW_INTERACT_EDIT,
    vendors: VIEW_INTERACT,
    contractors: FULL,
    portals: VIEW_ONLY,
    settings: NONE,
    control_plane: NONE,
    catalog: NONE,
  },

  admin: {
    command_center: FULL,
    command_dashboard: VIEW_ONLY,
    leads: VIEW_INTERACT_EDIT,
    conversations: VIEW_INTERACT_EDIT,
    follow_ups: VIEW_INTERACT_EDIT,
    appointments: VIEW_INTERACT_EDIT,
    appointment_results: VIEW_ONLY,
    opportunities: VIEW_INTERACT_EDIT,
    rep_scorecards: VIEW_ONLY,
    commissions: VIEW_ONLY,
    disposition_report: VIEW_ONLY,
    pricing: NONE,
    financials: VIEW_ONLY,
    accounting: VIEW_INTERACT,
    revenue_intelligence: NONE,
    earnings_intelligence: NONE,
    jobs: FULL,
    dispatch: VIEW_INTERACT_EDIT,
    scheduling: VIEW_INTERACT_EDIT,
    install_readiness: VIEW_INTERACT,
    issues: VIEW_INTERACT_EDIT,
    service_tickets: VIEW_INTERACT_EDIT,
    qa_form: VIEW_INTERACT_EDIT,
    reports: VIEW_ONLY,
    marketing: VIEW_ONLY,
    pipeline_intelligence: VIEW_ONLY,
    automations: VIEW_INTERACT_EDIT,
    warehouse: VIEW_INTERACT,
    inventory: VIEW_INTERACT,
    ordering: VIEW_INTERACT_EDIT,
    vendors: VIEW_INTERACT,
    contractors: FULL,
    portals: VIEW_INTERACT,
    settings: VIEW_INTERACT,
    control_plane: NONE,
    catalog: VIEW_INTERACT_EDIT,
  },

  cfo: {
    command_center: VIEW_ONLY,
    command_dashboard: NONE,
    leads: NONE,
    conversations: NONE,
    follow_ups: NONE,
    appointments: NONE,
    appointment_results: NONE,
    opportunities: VIEW_ONLY,
    rep_scorecards: NONE,
    commissions: VIEW_INTERACT,
    disposition_report: NONE,
    pricing: NONE,
    financials: VIEW_INTERACT,
    accounting: FULL,
    revenue_intelligence: VIEW_INTERACT,
    earnings_intelligence: VIEW_INTERACT,
    jobs: VIEW_ONLY,
    dispatch: NONE,
    scheduling: NONE,
    install_readiness: NONE,
    issues: NONE,
    service_tickets: NONE,
    qa_form: NONE,
    reports: VIEW_ONLY,
    marketing: NONE,
    pipeline_intelligence: NONE,
    automations: NONE,
    warehouse: NONE,
    inventory: NONE,
    ordering: NONE,
    vendors: NONE,
    contractors: NONE,
    portals: NONE,
    settings: NONE,
    control_plane: NONE,
    catalog: NONE,
  },

  finance_manager: {
    command_center: VIEW_ONLY,
    command_dashboard: NONE,
    leads: NONE,
    conversations: NONE,
    follow_ups: NONE,
    appointments: NONE,
    appointment_results: NONE,
    opportunities: VIEW_ONLY,
    rep_scorecards: NONE,
    commissions: VIEW_INTERACT,
    disposition_report: NONE,
    pricing: NONE,
    financials: VIEW_INTERACT,
    accounting: VIEW_INTERACT_EDIT,
    revenue_intelligence: VIEW_ONLY,
    earnings_intelligence: VIEW_INTERACT,
    jobs: VIEW_ONLY,
    dispatch: NONE,
    scheduling: NONE,
    install_readiness: NONE,
    issues: NONE,
    service_tickets: NONE,
    qa_form: NONE,
    reports: VIEW_ONLY,
    marketing: NONE,
    pipeline_intelligence: NONE,
    automations: NONE,
    warehouse: NONE,
    inventory: NONE,
    ordering: NONE,
    vendors: NONE,
    contractors: NONE,
    portals: NONE,
    settings: NONE,
    control_plane: NONE,
    catalog: NONE,
  },

  bookkeeper: {
    command_center: VIEW_ONLY,
    command_dashboard: NONE,
    leads: NONE,
    conversations: NONE,
    follow_ups: NONE,
    appointments: NONE,
    appointment_results: NONE,
    opportunities: VIEW_ONLY,
    rep_scorecards: NONE,
    commissions: NONE,
    disposition_report: NONE,
    pricing: NONE,
    financials: VIEW_ONLY,
    accounting: VIEW_INTERACT,
    revenue_intelligence: NONE,
    earnings_intelligence: NONE,
    jobs: VIEW_ONLY,
    dispatch: NONE,
    scheduling: NONE,
    install_readiness: NONE,
    issues: NONE,
    service_tickets: NONE,
    qa_form: NONE,
    reports: VIEW_ONLY,
    marketing: NONE,
    pipeline_intelligence: NONE,
    automations: NONE,
    warehouse: NONE,
    inventory: NONE,
    ordering: NONE,
    vendors: NONE,
    contractors: NONE,
    portals: NONE,
    settings: NONE,
    control_plane: NONE,
    catalog: NONE,
  },

  warehouse_manager: {
    command_center: VIEW_ONLY,
    command_dashboard: NONE,
    leads: NONE,
    conversations: NONE,
    follow_ups: NONE,
    appointments: NONE,
    appointment_results: NONE,
    opportunities: NONE,
    rep_scorecards: NONE,
    commissions: NONE,
    disposition_report: NONE,
    pricing: NONE,
    financials: NONE,
    accounting: NONE,
    revenue_intelligence: NONE,
    earnings_intelligence: NONE,
    jobs: VIEW_ONLY,
    dispatch: NONE,
    scheduling: NONE,
    install_readiness: NONE,
    issues: VIEW_INTERACT,
    service_tickets: VIEW_INTERACT,
    qa_form: NONE,
    reports: NONE,
    marketing: NONE,
    pipeline_intelligence: NONE,
    automations: NONE,
    warehouse: FULL,
    inventory: VIEW_INTERACT_EDIT,
    ordering: VIEW_ONLY,
    vendors: NONE,
    contractors: NONE,
    portals: NONE,
    settings: NONE,
    control_plane: NONE,
    catalog: NONE,
  },

  head_of_installation: {
    command_center: VIEW_ONLY,
    command_dashboard: NONE,
    leads: NONE,
    conversations: NONE,
    follow_ups: NONE,
    appointments: NONE,
    appointment_results: NONE,
    opportunities: NONE,
    rep_scorecards: NONE,
    commissions: NONE,
    disposition_report: NONE,
    pricing: NONE,
    financials: NONE,
    accounting: NONE,
    revenue_intelligence: NONE,
    earnings_intelligence: NONE,
    jobs: VIEW_INTERACT,
    dispatch: NONE,
    scheduling: VIEW_INTERACT_EDIT,
    install_readiness: VIEW_INTERACT,
    issues: VIEW_INTERACT_EDIT,
    service_tickets: VIEW_INTERACT,
    qa_form: VIEW_INTERACT_EDIT,
    reports: NONE,
    marketing: NONE,
    pipeline_intelligence: NONE,
    automations: NONE,
    warehouse: VIEW_ONLY,
    inventory: VIEW_ONLY,
    ordering: VIEW_ONLY,
    vendors: NONE,
    contractors: VIEW_ONLY,
    portals: NONE,
    settings: NONE,
    control_plane: NONE,
    catalog: NONE,
  },

  procurement_manager: {
    command_center: VIEW_ONLY,
    command_dashboard: NONE,
    leads: NONE,
    conversations: NONE,
    follow_ups: NONE,
    appointments: NONE,
    appointment_results: NONE,
    opportunities: NONE,
    rep_scorecards: NONE,
    commissions: NONE,
    disposition_report: NONE,
    pricing: NONE,
    financials: NONE,
    accounting: NONE,
    revenue_intelligence: NONE,
    earnings_intelligence: NONE,
    jobs: VIEW_ONLY,
    dispatch: NONE,
    scheduling: NONE,
    install_readiness: NONE,
    issues: VIEW_INTERACT,
    service_tickets: VIEW_INTERACT,
    qa_form: NONE,
    reports: NONE,
    marketing: NONE,
    pipeline_intelligence: NONE,
    automations: NONE,
    warehouse: VIEW_INTERACT,
    inventory: VIEW_INTERACT,
    ordering: VIEW_INTERACT_EDIT,
    vendors: VIEW_INTERACT_EDIT,
    contractors: NONE,
    portals: NONE,
    settings: NONE,
    control_plane: NONE,
    catalog: NONE,
  },

  inventory_manager: {
    command_center: VIEW_ONLY,
    command_dashboard: NONE,
    leads: NONE,
    conversations: NONE,
    follow_ups: NONE,
    appointments: NONE,
    appointment_results: NONE,
    opportunities: NONE,
    rep_scorecards: NONE,
    commissions: NONE,
    disposition_report: NONE,
    pricing: NONE,
    financials: NONE,
    accounting: NONE,
    revenue_intelligence: NONE,
    earnings_intelligence: NONE,
    jobs: VIEW_ONLY,
    dispatch: NONE,
    scheduling: NONE,
    install_readiness: NONE,
    issues: VIEW_INTERACT,
    service_tickets: VIEW_INTERACT,
    qa_form: NONE,
    reports: NONE,
    marketing: NONE,
    pipeline_intelligence: NONE,
    automations: NONE,
    warehouse: VIEW_INTERACT,
    inventory: VIEW_INTERACT_EDIT,
    ordering: NONE,
    vendors: VIEW_ONLY,
    contractors: NONE,
    portals: NONE,
    settings: NONE,
    control_plane: NONE,
    catalog: NONE,
  },

  it_staff: {
    command_center: FULL,
    command_dashboard: VIEW_ONLY,
    leads: VIEW_INTERACT_EDIT,
    conversations: VIEW_INTERACT_EDIT,
    follow_ups: VIEW_INTERACT_EDIT,
    appointments: VIEW_INTERACT_EDIT,
    appointment_results: VIEW_ONLY,
    opportunities: VIEW_INTERACT_EDIT,
    rep_scorecards: VIEW_ONLY,
    commissions: VIEW_ONLY,
    disposition_report: VIEW_ONLY,
    pricing: NONE,
    financials: VIEW_ONLY,
    accounting: VIEW_INTERACT,
    revenue_intelligence: NONE,
    earnings_intelligence: NONE,
    jobs: FULL,
    dispatch: VIEW_INTERACT_EDIT,
    scheduling: VIEW_INTERACT_EDIT,
    install_readiness: VIEW_INTERACT,
    issues: VIEW_INTERACT_EDIT,
    service_tickets: VIEW_INTERACT_EDIT,
    qa_form: VIEW_INTERACT_EDIT,
    reports: VIEW_ONLY,
    marketing: VIEW_ONLY,
    pipeline_intelligence: VIEW_ONLY,
    automations: VIEW_INTERACT_EDIT,
    warehouse: VIEW_INTERACT,
    inventory: VIEW_INTERACT,
    ordering: VIEW_INTERACT_EDIT,
    vendors: VIEW_INTERACT,
    contractors: FULL,
    portals: VIEW_INTERACT,
    settings: VIEW_INTERACT,
    control_plane: NONE,
    catalog: VIEW_INTERACT_EDIT,
  },

  installer: {
    command_center: VIEW_ONLY,
    command_dashboard: NONE,
    leads: NONE,
    conversations: NONE,
    follow_ups: NONE,
    appointments: NONE,
    appointment_results: NONE,
    opportunities: NONE,
    rep_scorecards: NONE,
    commissions: NONE,
    disposition_report: NONE,
    pricing: NONE,
    financials: NONE,
    accounting: NONE,
    revenue_intelligence: NONE,
    earnings_intelligence: NONE,
    jobs: VIEW_INTERACT,
    dispatch: NONE,
    scheduling: NONE,
    install_readiness: NONE,
    issues: VIEW_INTERACT,
    service_tickets: NONE,
    qa_form: VIEW_INTERACT_EDIT,
    reports: NONE,
    marketing: NONE,
    pipeline_intelligence: NONE,
    automations: NONE,
    warehouse: NONE,
    inventory: NONE,
    ordering: NONE,
    vendors: NONE,
    contractors: NONE,
    portals: NONE,
    settings: NONE,
    control_plane: NONE,
    catalog: NONE,
  },

  subcontractor: {
    command_center: VIEW_ONLY,
    command_dashboard: NONE,
    leads: NONE,
    conversations: NONE,
    follow_ups: NONE,
    appointments: NONE,
    appointment_results: NONE,
    opportunities: NONE,
    rep_scorecards: NONE,
    commissions: NONE,
    disposition_report: NONE,
    pricing: NONE,
    financials: NONE,
    accounting: NONE,
    revenue_intelligence: NONE,
    earnings_intelligence: NONE,
    jobs: VIEW_INTERACT,
    dispatch: NONE,
    scheduling: NONE,
    install_readiness: NONE,
    issues: VIEW_INTERACT,
    service_tickets: NONE,
    qa_form: VIEW_INTERACT_EDIT,
    reports: NONE,
    marketing: NONE,
    pipeline_intelligence: NONE,
    automations: NONE,
    warehouse: NONE,
    inventory: NONE,
    ordering: NONE,
    vendors: NONE,
    contractors: NONE,
    portals: NONE,
    settings: NONE,
    control_plane: NONE,
    catalog: NONE,
  },
};

// ── Public API ──

export function getModulePermissions(role: UserRole, module: ModuleKey): ModulePermissions {
  return PERMISSION_MATRIX[role]?.[module] ?? NONE;
}

export function canView(role: UserRole, module: ModuleKey): boolean {
  return getModulePermissions(role, module).view;
}

export function canInteract(role: UserRole, module: ModuleKey): boolean {
  return getModulePermissions(role, module).interact;
}

export function canEdit(role: UserRole, module: ModuleKey): boolean {
  return getModulePermissions(role, module).edit;
}

export function canApprove(role: UserRole, module: ModuleKey): boolean {
  return getModulePermissions(role, module).approve;
}

/** Get human-readable restriction reason for tooltip display */
export function getRestrictionReason(role: UserRole, module: ModuleKey): string | null {
  const perms = getModulePermissions(role, module);
  if (perms.approve) return null;
  if (perms.edit) return "You do not have override/approval permissions for this module.";
  if (perms.interact) return "You have read and interact access only. Editing is restricted.";
  if (perms.view) return "You have view-only access to this module.";
  return "This module is not available for your role.";
}

// ── Enforcement Policies ──

/** Results are locked after submission — no role can edit */
export function isResultLocked(_role: UserRole): boolean {
  return true; // All results are immutable after submission
}

/** Pricing is immutable for all non-admin roles */
export function canEditPricing(role: UserRole): boolean {
  return role === "super_admin" || role === "owner";
}

/** Commission calculations are system-only — no manual edits */
export function canModifyCommissions(_role: UserRole): boolean {
  return false; // Commissions are calculated, never manually modified
}

// ── Audit Log ──

export interface PermissionAuditEntry {
  id: string;
  timestamp: string;
  actor: string;
  actorRole: UserRole;
  action: "override" | "edit" | "approve" | "reject" | "escalate";
  module: ModuleKey;
  targetId?: string;
  details: string;
  previousValue?: string;
  newValue?: string;
}

const _auditLog: PermissionAuditEntry[] = [];

export function logPermissionAction(entry: Omit<PermissionAuditEntry, "id" | "timestamp">): PermissionAuditEntry {
  const fullEntry: PermissionAuditEntry = {
    ...entry,
    id: `PERM-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
    timestamp: new Date().toISOString(),
  };
  _auditLog.push(fullEntry);
  return fullEntry;
}

export function getPermissionAuditLog(): PermissionAuditEntry[] {
  return [..._auditLog];
}
